'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { TableCell, TableHead, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, ArrowUpDown } from '@mynaui/icons-react';
import {
  ColumnDef,
  Row,
  SortDirection,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { HTMLAttributes, MouseEvent, forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { ColumnFilter } from './DataTableFilter';

const ROWS_TO_LOAD_MORE = 50;

const HighlightText = ({ text, highlight }: { text: string; highlight: string }) => {
  if (!highlight) return <>{text}</>;
  const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <mark key={i} className='bg-yellow-500/80 dark:bg-yellow-700 dark:text-white rounded-xs'>
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
};

// Filter types
export interface FilterOption {
  label: string;
  value: string;
}

export type ColumnFilterConfig = { type: 'options'; options: FilterOption[] } | { type: 'text' };

export type FiltersConfig = Record<string, ColumnFilterConfig>;

export type ColumnFilterValue = { type: 'options'; values: string[] } | { type: 'text'; value: string };

export type FiltersState = Record<string, ColumnFilterValue>;

const TableComponent = forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
  ),
);
TableComponent.displayName = 'TableComponent';

const LoadingRow = ({ colSpan }: { colSpan: number }) => (
  <TableRow>
    <TableCell colSpan={colSpan} className='text-center py-4'>
      <div className='flex items-center justify-center gap-2'>
        <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        <span className='text-sm text-muted-foreground'>Loading more...</span>
      </div>
    </TableCell>
  </TableRow>
);

function computeShiftSelection<TData>(
  rows: Row<TData>[],
  currentSelection: string[],
  clickedIndex: number,
  selectionKey: string,
): string[] {
  const selectedIndices: number[] = [clickedIndex];
  for (let i = 0; i < rows.length; i++) {
    const id = (rows[i].original as Record<string, any>)[selectionKey] as string;
    if (currentSelection.includes(id)) {
      selectedIndices.push(i);
    }
  }

  const minIndex = Math.min(...selectedIndices);
  const maxIndex = Math.max(...selectedIndices);

  const newSelection: string[] = [];
  for (let i = minIndex; i <= maxIndex; i++) {
    const id = (rows[i].original as Record<string, any>)[selectionKey] as string;
    newSelection.push(id);
  }

  return newSelection;
}

interface TableRowComponentOptions<TData> {
  rows: Row<TData>[];
  isLoadingMore: boolean;
  columnCount: number;
  selection?: string[];
  onSelect?: (selection: string[]) => void;
  selectionKey?: string;
  highlightText?: string;
  onRowContextMenu?: (event: MouseEvent<HTMLTableRowElement>, row: TData) => void;
}

const TableRowComponent =
  <TData,>({
    rows,
    isLoadingMore,
    columnCount,
    selection,
    onSelect,
    selectionKey,
    highlightText,
    onRowContextMenu,
  }: TableRowComponentOptions<TData>) =>
  (props: HTMLAttributes<HTMLTableRowElement>) => {
    // @ts-expect-error data-index is a valid attribute
    const index = props['data-index'];

    // If this is the last index and we're loading more, show loading row
    if (index === rows.length && isLoadingMore) {
      return <LoadingRow colSpan={columnCount} />;
    }

    const row = rows[index];
    if (!row) return null;

    const handleRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
      if (!selection || !onSelect || !selectionKey) return;
      const value = (row.original as Record<string, any>)[selectionKey] as string;

      if (event.shiftKey) {
        onSelect(computeShiftSelection(rows, selection, index, selectionKey));
      } else if (selection.includes(value)) {
        onSelect(selection.filter((id) => id !== value));
      } else {
        onSelect([...selection, value]);
      }
    };

    return (
      <TableRow
        key={row.id}
        data-state={row.getIsSelected() && 'selected'}
        onClick={onSelect ? handleRowClick : undefined}
        onContextMenu={
          onRowContextMenu
            ? (e) => {
                e.preventDefault();
                onRowContextMenu(e, row.original);
              }
            : undefined
        }
        className={onSelect ? 'cursor-pointer' : undefined}
        {...props}>
        {row.getVisibleCells().map((cell) => {
          const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
          const rawValue = cell.getValue();
          const stringValue =
            typeof rawValue === 'string' ? rawValue : typeof rawValue === 'number' ? String(rawValue) : null;
          const shouldHighlight =
            highlightText && stringValue && stringValue.toLowerCase().includes(highlightText.toLowerCase());
          return (
            <TableCell key={cell.id}>
              {shouldHighlight ? <HighlightText text={stringValue!} highlight={highlightText} /> : rendered}
            </TableCell>
          );
        })}
      </TableRow>
    );
  };

const SortingIndicator = ({ isSorted }: { isSorted: SortDirection | false }) => {
  const icon = useMemo(() => {
    if (!isSorted) return <ArrowUpDown className='size-5' />;
    if (isSorted === 'asc') return <ArrowUp className='size-3.5 stroke-2' />;
    if (isSorted === 'desc') return <ArrowDown className='size-3.5 stroke-2' />;
    return null;
  }, [isSorted]);
  return <div className='size-6 flex items-center justify-center'>{icon}</div>;
};

interface DataTableProps<TData, TValue> {
  loading?: boolean;
  total?: number;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  // Infinite loading props
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void | Promise<void>;
  // Sorting props (controlled from parent)
  sorting?: SortingState;
  onSortingChange?: (updater: SortingState | ((old: SortingState) => SortingState)) => void;
  // Selection props
  selection?: string[];
  onSelect?: (selection: string[]) => void;
  selectionKey?: string;
  // Filter props (controlled from parent)
  filtersConfig?: FiltersConfig;
  filters?: FiltersState;
  onFilterChange?: (filters: FiltersState) => void;
  // Highlight text in cells
  highlightText?: string;
  // Context menu
  onRowContextMenu?: (event: MouseEvent<HTMLTableRowElement>, row: TData) => void;
}

export const DataTable = <TData, TValue>({
  total = 0,
  columns,
  data,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  sorting = [],
  onSortingChange,
  selection,
  onSelect,
  selectionKey = 'id',
  filtersConfig,
  filters = {},
  onFilterChange,
  highlightText,
  onRowContextMenu,
}: DataTableProps<TData, TValue>) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(() => wrapperRef.current?.clientHeight || 0);
  const loadingRef = useRef(false);

  useLayoutEffect(() => {
    setHeight(wrapperRef.current?.clientHeight || 0);
  }, [wrapperRef.current?.clientHeight]);

  const allColumns = useMemo(() => {
    if (!selection || !onSelect) return columns;

    const allSelected = selection.length === total;
    const someSelected = selection.length > 0 && !allSelected;

    const selectionColumn: ColumnDef<TData, TValue> = {
      id: 'selection',
      size: 10,
      minSize: 10,
      maxSize: 10,
      enableSorting: false,
      header: () => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={allSelected || someSelected}
            indeterminate={someSelected}
            onCheckedChange={() => {
              if (allSelected) {
                onSelect([]);
              } else {
                onSelect(data.map((row) => (row as Record<string, any>)[selectionKey] as string));
              }
            }}
          />
        </div>
      ),
      cell: ({ row }) => {
        const value = (row.original as Record<string, any>)[selectionKey] as string;
        const isSelected = selection.includes(value);
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => {
                if (isSelected) {
                  onSelect(selection.filter((id) => id !== value));
                } else {
                  onSelect([...selection, value]);
                }
              }}
            />
          </div>
        );
      },
    };

    return [selectionColumn, ...columns];
  }, [columns, selection, onSelect, selectionKey, total, data]);

  const table = useReactTable({
    data,
    columns: allColumns,
    state: {
      sorting,
    },
    onSortingChange: onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true, // Disable client-side sorting since we're doing server-side sorting
  });

  const { rows } = table.getRowModel();
  const columnCount = table.getAllColumns().filter((col) => col.getIsVisible()).length;

  const _handleEndReached = () => {
    if (!hasMore || isLoadingMore || loadingRef.current || !onLoadMore) return;

    loadingRef.current = true;
    const result = onLoadMore();

    // Handle both sync and async callbacks
    if (result instanceof Promise) {
      result.finally(() => {
        loadingRef.current = false;
      });
    } else {
      loadingRef.current = false;
    }
  };

  const _handleRangeChanged = ({ endIndex }: { endIndex: number }) => {
    if (endIndex >= rows.length - ROWS_TO_LOAD_MORE) _handleEndReached();
  };

  // Calculate total count including loading row
  const totalCount = total || rows.length + (hasMore && isLoadingMore ? 1 : 0);

  return (
    <div className='rounded-md border flex-1' ref={wrapperRef}>
      <TableVirtuoso
        style={{ height }}
        totalCount={totalCount}
        // endReached={_handleEndReached}
        overscan={100}
        rangeChanged={_handleRangeChanged}
        components={{
          Table: TableComponent,
          TableRow: TableRowComponent({
            rows,
            isLoadingMore,
            columnCount,
            selection,
            onSelect,
            selectionKey,
            highlightText,
            onRowContextMenu,
          }),
        }}
        fixedHeaderContent={() =>
          table.getHeaderGroups().map((headerGroup) => (
            // Change header background color to non-transparent
            <TableRow className='border-b border-gray-200' key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const filterConfig = filtersConfig?.[header.column.id];
                const isFiltered = !!filters[header.column.id];
                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className='bg-white dark:bg-stone-900 first:rounded-tl-md last:rounded-tr-md border-b p-0'
                    width={header.getSize()}
                    style={{
                      width: header.getSize(),
                    }}>
                    <div className='group h-full w-full border-b border-stone-200/70 dark:border-stone-800/70 flex items-center px-2'>
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            className='flex items-center w-full'
                            {...{
                              style: header.column.getCanSort()
                                ? {
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                  }
                                : {},
                              onClick: header.column.getToggleSortingHandler(),
                            }}>
                            <div
                              className={cn(
                                'flex-1 flex items-center rounded-md pl-2 -ml-2 pr-1 h-8',

                                header.column.getCanSort() ? 'hover:bg-accent/50' : '',
                              )}>
                              <div className='flex-1'>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </div>
                              {header.column.getCanSort() && (
                                <SortingIndicator isSorted={header.column.getIsSorted()} />
                              )}
                            </div>

                            <div className='flex items-center ml-auto'>
                              {filterConfig && onFilterChange && (
                                <div
                                  className={cn('ml-auto flex items-center transition-opacity')}
                                  onClick={(e) => e.stopPropagation()}>
                                  <ColumnFilter
                                    columnId={header.column.id}
                                    config={filterConfig}
                                    filters={filters}
                                    onFilterChange={onFilterChange}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))
        }
      />
    </div>
  );
};
